// src/context/AuthContext.jsx
// ✅ Permessi custom dal ruolo assegnato (canManageProjects, canViewFinancials, ecc.)
// ✅ Dashboard si adatta automaticamente ai permessi — nessun ruolo hardcoded
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { authApi } from '../api/authApi';
import { organizationApi } from '../api/organizationApi';
import { subscriptionApi } from '../api/subscriptionApi';
import { STORAGE_KEYS, PLANS } from '../utils/constants';
import { identifyUser, resetAnalytics } from '../utils/analytics';
import toast from 'react-hot-toast';
import i18n from '../i18n/i18n';

const AuthContext = createContext(null);

// ==================== PERMESSI DEFAULT PER RUOLO STANDARD ====================
// Usati quando l'utente NON ha un ruolo custom con permessi specifici
export const ROLE_DEFAULT_PERMISSIONS = {
  SUPER_ADMIN: {
    canManageProjects: true, canViewFinancials: true, canManageExpenses: true,
    canValidateExpenses: true, canViewReports: true, canInviteUsers: true,
    canManageTeam: true, canManageOrganization: true, canAccessAI: true,
  },
  SITE_MANAGER: {
    canManageProjects: true, canViewFinancials: true, canManageExpenses: true,
    canValidateExpenses: true, canViewReports: true, canInviteUsers: true,
    canManageTeam: true, canManageOrganization: false, canAccessAI: true,
  },
  ENGINEER: {
    canManageProjects: true, canViewFinancials: false, canManageExpenses: true,
    canValidateExpenses: false, canViewReports: true, canInviteUsers: false,
    canManageTeam: false, canManageOrganization: false, canAccessAI: true,
  },
  ACCOUNTANT: {
    canManageProjects: false, canViewFinancials: true, canManageExpenses: true,
    canValidateExpenses: true, canViewReports: true, canInviteUsers: false,
    canManageTeam: false, canManageOrganization: false, canAccessAI: false,
  },
  FIELD_SUPERVISOR: {
    canManageProjects: true, canViewFinancials: false, canManageExpenses: true,
    canValidateExpenses: false, canViewReports: false, canInviteUsers: false,
    canManageTeam: false, canManageOrganization: false, canAccessAI: false,
  },
  VIEWER: {
    canManageProjects: false, canViewFinancials: false, canManageExpenses: false,
    canValidateExpenses: false, canViewReports: true, canInviteUsers: false,
    canManageTeam: false, canManageOrganization: false, canAccessAI: false,
  },
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [permissions, setPermissions] = useState(null); // ✅ permessi ruolo custom
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isMounted = useRef(true);
  const hasFetched = useRef(false);
  const isFetching = useRef(false);

  const loadOrganizationData = useCallback(async () => {
    if (isFetching.current || !isMounted.current) return;
    isFetching.current = true;
    try {
      const [orgRes, subRes, statsRes] = await Promise.all([
        organizationApi.getMy(),
        subscriptionApi.getCurrent(),
        organizationApi.getStats(),
      ]);
      if (!isMounted.current) return;
      const orgData = orgRes.data;
      const subData = subRes.data;
      const effectivePlan = subData.subscription?.plan || orgData.plan || PLANS.FREE;
      setOrganization({ ...orgData, plan: effectivePlan });
      setSubscription(subData.subscription || null);
      setPlanLimits(subData.planLimits || null);
      setUsage({
        ...(statsRes.data || {}),
        storageUsedMb: statsRes.data?.storageUsedMb ?? statsRes.data?.storageUsed ?? 0,
      });
    } catch (err) {
      console.error('[AuthContext] loadOrganizationData:', err);
    } finally {
      isFetching.current = false;
    }
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) { setLoading(false); return; }
    try {
      const response = await authApi.getMe();
      if (!isMounted.current) return;
      const userData = response.data;
      setUser(userData);
      // ✅ Salva permessi custom se presenti
      if (userData.permissions) setPermissions(userData.permissions);
      if (userData.organizationId) await loadOrganizationData();
    } catch (err) {
      console.error('[AuthContext] loadUser:', err);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      if (isMounted.current) setError(i18n.t('auth.sessionExpired'));
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [loadOrganizationData]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  // ==================== AUTH ACTIONS ====================

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authApi.login({ email, password });
      const loggedUser = response.data.user;
      setUser(loggedUser);
      if (loggedUser.permissions) setPermissions(loggedUser.permissions);
      hasFetched.current = true;
      isFetching.current = false;
      await loadOrganizationData();
      try { identifyUser(loggedUser, loggedUser.organization); } catch { }
      toast.success(i18n.t('auth.loginSuccess'));
      return { success: true };
    } catch (err) {
      const message = err.message || i18n.t('auth.loginError');
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authApi.register(userData);
      toast.success(i18n.t('auth.registerSuccess'));
      return { success: true, data: response.data };
    } catch (err) {
      const message = err.message || i18n.t('auth.registerError');
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    try { resetAnalytics(); } catch { }
    authApi.logout();
    setUser(null);
    setOrganization(null);
    setSubscription(null);
    setPlanLimits(null);
    setUsage(null);
    setPermissions(null);
    hasFetched.current = false;
    isFetching.current = false;
  };

  const refreshOrganization = useCallback(async () => {
    isFetching.current = false;
    await loadOrganizationData();
  }, [loadOrganizationData]);

  // ==================== COMPUTED ====================

  const _isUnlimited = (val) =>
    val === -1 || val === null || val === undefined || val === Infinity || val >= 999999;

  const effectivePlan = subscription?.plan || organization?.plan || PLANS.FREE;
  const isFree = effectivePlan === PLANS.FREE;
  const isStarter = effectivePlan === PLANS.STARTER;
  const isPro = [PLANS.PRO, PLANS.ENTERPRISE].includes(effectivePlan);
  const isEnterprise = effectivePlan === PLANS.ENTERPRISE;
  const isSuperAdmin = subscription?.isSuperAdmin === true || user?.role === 'SUPER_ADMIN';

  const hasFeature = useCallback((feature) => {
    if (isSuperAdmin) return true;
    if (!planLimits) return false;
    return planLimits[feature] === true;
  }, [planLimits, isSuperAdmin]);

  // ✅ Controlla permesso:
  // 1) SUPER_ADMIN → sempre true
  // 2) Permessi custom (ruolo custom con permessi specifici) → usa quelli
  // 3) Permessi default del ruolo standard (SITE_MANAGER, ENGINEER, ecc.)
  const can = useCallback((permission) => {
    if (isSuperAdmin) return true;
    if (permissions) return permissions[permission] === true;
    // Fallback: permessi default basati sul ruolo standard
    const role = user?.role;
    const defaults = ROLE_DEFAULT_PERMISSIONS[role] || {};
    return defaults[permission] === true;
  }, [permissions, isSuperAdmin, user]);

  const canCreate = useCallback((resource) => {
    if (isSuperAdmin) return true;
    if (!usage || !planLimits) return true;
    const map = {
      project: { used: usage.projectsCount, max: planLimits.maxProjects },
      user: { used: usage.usersCount, max: planLimits.maxUsers },
      expense: { used: usage.expensesCount, max: planLimits.maxExpenses },
    };
    const entry = map[resource];
    if (!entry) return true;
    if (_isUnlimited(entry.max)) return true;
    return entry.used < entry.max;
  }, [usage, planLimits, isSuperAdmin]);

  const canCreateProject = useCallback(() => canCreate('project'), [canCreate]);
  const canInviteUser = useCallback(() => canCreate('user'), [canCreate]);
  const canAddExpense = useCallback(() => canCreate('expense'), [canCreate]);

  const usagePercent = useCallback((resource) => {
    if (!usage || !planLimits) return null;
    const map = {
      project: { used: usage.projectsCount, max: planLimits.maxProjects },
      user: { used: usage.usersCount, max: planLimits.maxUsers },
      expense: { used: usage.expensesCount, max: planLimits.maxExpenses },
    };
    const entry = map[resource];
    if (!entry || _isUnlimited(entry.max)) return null;
    return Math.min(100, Math.round((entry.used / entry.max) * 100));
  }, [usage, planLimits]);

  const value = {
    user, organization, subscription, planLimits, usage, loading, error,
    permissions, // ✅ permessi granulari ruolo custom
    login, register, logout, refreshOrganization,
    isAuthenticated: !!user,
    isFree, isStarter, isPro, isEnterprise, isSuperAdmin, effectivePlan,
    hasFeature,
    can,           // ✅ can('canManageProjects'), can('canViewFinancials'), ecc.
    canCreate, canCreateProject, canInviteUser, canAddExpense, usagePercent,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};